import {service, BasicService} from 'nova';
import {stringify} from '@acng/frontend-bounty/std/string.js';
import {Error} from '@acng/frontend-bounty/std/error.js';
import {sleep} from '@acng/frontend-bounty/sleep.js';
import {guard, STRING} from '@acng/frontend-rubicon';
import {discardToken, useToken} from '../userPool/service/token.js';
import {handleCfChallenge} from '../core/service/backend/cf-challenge.js';

/**
 * @typedef ObrienConfig
 *
 * @property {string} baseURL
 */

/**
 * @extends {BasicService<'obrien', ObrienConfig>}
 */
@service
class ObrienClient extends BasicService {
  /**
   * @type {Promise<string> | undefined}
   */
  #vapid;

  constructor() {
    super('obrien');
  }

  get vapid() {
    return (this.#vapid ??= this.#getVAPID());
  }

  get #baseURL() {
    if (!this.config) {
      throw Error('no config');
    }

    return this.config.baseURL;
  }

  /**
   * Get the VAPID key for the current portal.
   *
   * @returns {Promise<string>}
   */
  async #getVAPID() {
    const token = await useToken();
    const response = await fetch(`${this.#baseURL}/vapidkey`, {headers: {'x-token': token}});
    const {ok, status} = response;
    handleCfChallenge(response);

    if (!ok) {
      if (status == 401) {
        discardToken(token);
        await sleep(1000);

        return this.#getVAPID();
      }

      throw Error('failed to get vapid key', {cause: response});
    }

    const data = await response.json();
    ASSERT: guard(data, STRING);
    return data;
  }

  /**
   * @param {PushSubscription} sub
   * @param {string} vapid
   *
   * @returns {Promise<void>}
   */
  async subscribe(sub, vapid) {
    const token = await useToken();

    const response = await fetch(`${this.#baseURL}/subscription`, {
      method: 'POST',
      body: stringify(sub),
      headers: {
        'content-type': 'application/json',
        'x-token': token,
        'x-key': vapid,
      },
    });

    const {ok, status} = response;
    handleCfChallenge(response);

    if (!ok) {
      if (status == 401) {
        discardToken(token);
        await sleep(1000);

        return this.subscribe(sub, vapid);
      }

      throw response;
    }
  }

  /**
   * @param {PushSubscription} sub
   *
   * @returns {Promise<void>}
   */
  async unsubscribe(sub) {
    const token = await useToken();

    const response = await fetch(`${this.#baseURL}/subscription`, {
      method: 'DELETE',
      body: JSON.stringify(sub),
      headers: {
        'content-type': 'application/json',
        'x-token': token,
      },
    });

    const {ok, status} = response;
    handleCfChallenge(response);

    if (!ok) {
      if (status == 401) {
        discardToken(token);
        await sleep(1000);

        return this.unsubscribe(sub);
      }

      throw response;
    }
  }
}

export {ObrienClient};
